import { AssociationsList, Card, Markdown, TextLink } from 'components';
import { userValueFormat } from 'helpers';
import { GutCstPieChart, Typography } from '@repo/ui';

import { useCompositionType } from 'services/microbiome/useCompositionType';

import c from 'classnames';
import styles from './GutCstCard.module.css';
import useGutType from 'services/gutType/useGutType';
import { useMemo, useState } from 'react';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { getGutMicrobiomeColor } from '@repo/utils';

export const GutCstCard = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { compositionType } = useCompositionType();
  const { currentKitType } = useCurrentKitAndTinyAccount();

  const { data: currentKitGutData } = useGutType();

  const source = useMemo(
    () =>
      !currentKitGutData
        ? ''
        : currentKitGutData?.gut_vaginal_type?.long_description?.replace(
            /\\n/gi,
            '\n \n',
          ),
    [currentKitGutData],
  );

  const associations = useMemo(() => {
    return currentKitGutData?.gut_vaginal_type?.associations_list ?? [];
  }, [currentKitGutData]);

  const compositionTypeData = compositionType.data;

  const { gutVaginalType, composition } = compositionTypeData;

  const gutVaginalTypePieData =
    !!gutVaginalType && 'type_bact' in gutVaginalType
      ? [
          {
            value: gutVaginalType?.type_bact,
            name: 'Bacteroides',
          },
          {
            value: gutVaginalType?.type_rumino,
            name: 'Ruminococcaceae',
          },
          {
            value: gutVaginalType?.type_prevotella,
            name: 'Prevotella',
          },
          {
            value: gutVaginalType?.type_lachno,
            name: 'Lachnospiraceae',
          },
          {
            value: gutVaginalType?.type_ent,
            name: 'Enterobacteriaceae',
          },
          {
            value: gutVaginalType?.type_bif,
            name: 'Bifidobacterium',
          },
          {
            value: gutVaginalType?.type_entcoc,
            name: 'Enterococcus',
          },
          {
            value: gutVaginalType?.type_strep,
            name: 'Streptococcus',
          },
          {
            value: gutVaginalType?.type_staph,
            name: 'Staphylococcus',
          },
          {
            value: gutVaginalType?.type_other,
            name: 'Other',
          },
        ]
      : //Vaginal type
        [
          {
            value: (composition?.beneficial ?? 0) / 100,
            name: 'Protective',
          },
          {
            value: (composition?.unfriendly ?? 0) / 100,
            name: 'Disruptive',
          },
          {
            value: (composition?.variable ?? 0) / 100,
            name: 'Variable',
          },
          {
            value: (composition?.unknown ?? 0) / 100,
            name: 'Unknown',
          },
        ];

  const toggleExpand = () => {
    setIsExpanded(prev => !prev);
  };

  return (
    <Card variant='wrapper' className={styles.card}>
      <div className={styles.cardContainer}>
        <div className={styles.cardHeader}>
          <Typography variant='body-s'>{`${
            currentKitType === 'vaginal'
              ? 'Community State Type (CST)'
              : 'Gut Type'
          }`}</Typography>
        </div>
        <Typography variant='heading-xl'>
          {`Type ${currentKitGutData?.gut_vaginal_type?.display_name}`}
        </Typography>

        <GutCstPieChart
          gutVaginalTypePieData={gutVaginalTypePieData ?? []}
          userValueFormat={userValueFormat}
        />
        {source && (
          <div>
            <div
              className={c(
                styles.descriptionContainer,
                !isExpanded && styles.collapsed,
              )}
            >
              <Markdown>{source}</Markdown>
            </div>
            <TextLink
              className={styles.expandButton}
              onClick={toggleExpand}
              label={isExpanded ? 'Less' : 'More'}
            />
          </div>
        )}

        {!!associations?.length && (
          <div className={styles.associationsContainer}>
            <Typography variant='heading-m'>
              This Microbiome Type could be linked to:
            </Typography>
            <AssociationsList associations={associations} />
          </div>
        )}
      </div>
    </Card>
  );
};
